import React, { useCallback, useState } from 'react';
import { View } from 'react-native';
import { useRouter } from 'expo-router';
import { Query } from '@/utils/react-query/Queries';
import ActivityFeed from '@/components/Activity/ActivityFeed';
import TableFilters from '@/components/General/TableFilters';
import { getActivityRoute } from '@/utils/buildActivityDetails';

/**
 * Organization Activity Log
 * Shows all activity across the organization using the new Activity table
 */

const ActivityLogScreen = () => {
    const router = useRouter();
    const { data: loggedInUser } = Query.people.loggedInUser();
    const canViewContributions = loggedInUser?.can?.('View Contributions');

    // Filter state
    const [filters, setFilters] = useState([
        {
            label: 'User',
            inputType: 'sheetselector',
            sheetType: 'people',
            value: null,
        },
        {
            label: 'Resource',
            inputType: 'dropdown',
            options: [
                { label: 'All', value: null },
                { label: 'Person', value: 'Person' },
                { label: 'Event', value: 'Event' },
                { label: 'Event Instance', value: 'EventInstance' },
                { label: 'Task', value: 'Task' },
                { label: 'Small Group', value: 'SmallGroup' },
                { label: 'Path', value: 'Path' },
                { label: 'Family', value: 'Family' },
                ...(canViewContributions ? [{ label: 'Contribution', value: 'Contribution' }] : []),
                { label: 'Training', value: 'Training' },
                { label: 'Form', value: 'Form' },
            ],
            value: null,
        },
        {
            label: 'Action',
            inputType: 'dropdown',
            options: [
                { label: 'All', value: null },
                { label: 'Created', value: 'create' },
                { label: 'Updated', value: 'update' },
                { label: 'Deleted', value: 'delete' },
                { label: 'Assigned', value: 'assign' },
                { label: 'Unassigned', value: 'unassign' },
                { label: 'Published', value: 'publish' },
                { label: 'Closed', value: 'close' },
                { label: 'Reopened', value: 'reopen' },
                { label: 'Archived', value: 'archive' },
                { label: 'Unarchived', value: 'unarchive' },
            ],
            value: null,
        },
        {
            label: 'Date Range',
            inputType: 'daterange',
            value: null,
        },
    ]);

    const [searchText, setSearchText] = useState('');

    // Build filter object for API
    const buildApiFilters = useCallback(() => {
        const apiFilters = {};
        filters.forEach(filter => {
            if (filter.value) {
                switch (filter.label) {
                    case 'User':
                        // SheetSelector returns full person object, extract just the ID
                        apiFilters.actorID = typeof filter.value === 'object' ? filter.value.id : filter.value;
                        break;
                    case 'Resource':
                        apiFilters.subjectType = filter.value;
                        break;
                    case 'Action':
                        apiFilters.verb = filter.value;
                        break;
                    case 'Date Range':
                        if (filter.value.start) apiFilters.dateFrom = filter.value.start;
                        if (filter.value.end) apiFilters.dateTo = filter.value.end;
                        break;
                }
            }
        });
        if (searchText) {
            apiFilters.search = searchText;
        }
        return apiFilters;
    }, [filters, searchText]);

    const apiFilters = buildApiFilters();

    // Query with filters
    const activityQuery = Query.activity.feed({ filters: apiFilters });

    const handleActivityPress = useCallback((activity) => {
        const route = getActivityRoute(activity);
        if (route) {
            router.push(route);
        }
    }, [router]);

    const handleFiltersChange = useCallback((newFilters) => {
        setFilters(newFilters);
    }, []);

    const handleSearchChange = useCallback((text) => {
        setSearchText(text);
    }, []);

    const handleClearFilters = useCallback(() => {
        setFilters(filters.map(f => ({ ...f, value: null })));
        setSearchText('');
    }, [filters]);

    return (
        <View style={{ flex: 1 }}>
            <View style={{ paddingHorizontal: 16 }}>
                <TableFilters
                    tableName="activity-log"
                    filters={filters}
                    onFiltersChange={handleFiltersChange}
                    onSearchChange={handleSearchChange}
                    onClearFilters={handleClearFilters}
                />
            </View>
            <ActivityFeed
                query={activityQuery}
                onActivityPress={handleActivityPress}
                emptyMessage="No activity recorded yet"
            />
        </View>
    );
};

export default ActivityLogScreen